import {
    inject,
    Injectable
} from '@angular/core';

import {
    EntityManagerService,
    EnvironmentInfoService,
    ZIdentityService
} from 'src/app/core';

import {
    PaymentMethods
} from 'tilby-models';

import {
    ZPMSConnector
} from './z-pms-connector.model';

export type ZPMSConfig = {
    username?: string
    password?: string
    apiKey?: string
    connectedPms: ZPMSConnector.LoginDto['connectedProduct'],
    pmsFeatures?: ZPMSConnector.SupportedPmsItem
    address?: string
    propertyId?: string
    stagingMode?: boolean
}

export class ZPMSApi {
    constructor(
        private zIdentityService: ZIdentityService,
        private environmentInfoService: EnvironmentInfoService,
        private config: ZPMSConfig,
    ) {
    }

    /**
     * Generates the login payload for the ZPMS Connector API.
     * @param serialNumber The serial number of the device.
     * @returns A promise that resolves to the login DTO.
     */
    private async getLoginPayload(serialNumber: string): Promise<ZPMSConnector.LoginDto> {
        if (!this.config.connectedPms) {
            return {} as ZPMSConnector.LoginDto;
        }

        return {
            address: this.config.address,
            apiKey: this.config.apiKey,
            basicAuthCredentials: (this.config.username && this.config.password) ? {
                user: this.config.username,
                password: this.config.password
            } : undefined,
            connectedProduct: this.config.connectedPms,
            propertyId: this.config.propertyId,
            serialNumber: serialNumber
        }
    }

    /**
     * Sends an API request to the ZPMS Connector.
     * @template Output The expected type of the response.
     * @param endpointUrl The endpoint URL relative to the base API URL.
     * @param method The HTTP method (e.g., 'GET', 'POST', 'DELETE').
     * @param data The request body data for POST/PUT requests.
     * @param params Query parameters to be appended to the URL.
     * @param options Additional options for the request.
     * @param options.skipLogin Whether to remove the login payload.
     * @returns A promise that resolves to the API response.
     */
    private async sendApiRequest<Output = any>(endpointUrl: string, method: string, data?: any, params: any = {}, options?: { skipLogin?: boolean }): Promise<Output> {
        await this.zIdentityService.init();

        const headers = new Headers({
            'Content-Type': 'application/json',
            'Authorization': `${this.zIdentityService.getTokenType()} ${this.zIdentityService.getAccessToken()}`,
            'X-Subscription-Key': this.zIdentityService.getSubscriptionKey()
        });

        const apiUrl = 'https://api.zucchetti-integration.com/zposconnector/v1/';

        const url = new URL(endpointUrl, apiUrl);

        const deviceMeta = await this.environmentInfoService.getDeviceMeta();

        const loginPayload = options?.skipLogin ? {} : await this.getLoginPayload(deviceMeta.client_uuid);

        const deviceData: ZPMSConnector.DeviceDto = {
            id: deviceMeta.client_uuid,
            name: deviceMeta.device_name
        };

        data = Object.assign(data || {}, loginPayload, data ? { device: deviceData } : {});

        for (const key in params) {
            url.searchParams.set(key, params[key]);
        }

        const response = await fetch(url, {
            method: method,
            headers: headers,
            body: data && method !== 'GET' ? JSON.stringify(data, (key: string, value: unknown) => {
                if (value !== null) {
                    return value;
                }

                return undefined;
            }) : null,
            signal: AbortSignal.timeout(60000)
        }).catch(err => {
            throw undefined;
        });

        if (!response.ok) {
            let errorBody: any = await response.text();

            try {
                errorBody = JSON.parse(errorBody);
            } catch (err) {
                throw errorBody;
            }

            if (errorBody && typeof errorBody === 'object') {
                if ('internalError' in errorBody && errorBody.internalError?.message) {
                    throw errorBody.internalError.message;
                }

                if ('error' in errorBody) {
                    throw errorBody.error;
                }
            }

            throw JSON.stringify(errorBody);
        }

        const res = await response.json() as (Object | ZPMSConnector.ErrorResponse);

        if (res && 'internalError' in res && (res as any).internalError?.message) {
            throw (res as any).internalError.message;
        }

        return res as Output;
    }

    /**
     * Retrieves the configured property ID.
     * @returns The property ID.
     */
    public getPropertyId(): string {
        return this.config.propertyId!;
    }

    /**
     * Retrieves the configured connected PMS.
     * @returns The connected PMS.
     */
    public getConnectedPms(): string {
        return this.config.connectedPms!;
    }

    /**
     * Retrieves the list of supported PMS.
     * @returns A promise that resolves to a record of supported PMS.
     */
    public async getSupportedPMS() {
        return await this.sendApiRequest<ZPMSConnector.SupportedPMSResponse>('supportedpms', 'GET');
    }
    
    /**
     * Retrieves reservations from the PMS.
     * @returns A promise that resolves to an array of folios sorted by key door code.
     */
    public async getReservations() {
        const response = await this.sendApiRequest<ZPMSConnector.FolioResponse>('reservations', 'POST');

        return (response.folios || []).sort((a, b) => a.keyDoorCode! > b.keyDoorCode! ? 1 : -1);
    }

    /**
     * Retrieves miscellaneous sales from the PMS.
     * @returns A promise that resolves to an array of folios.
     */
    public async getMiscSales() {
        // If the feature is disabled for the PMS, return an empty array
        if(this.config.pmsFeatures?.operations?.['/miscsales'] !== true) {
            return undefined;
        }

        const response = await this.sendApiRequest<ZPMSConnector.FolioResponse>('miscsales', 'POST');

        return response.folios || [];
    }

    /**
     * Creates a charge in the PMS.
     * @param transactionData The sales resource data for the charge.
     * @returns A promise that resolves to the transaction response.
     */
    public async createCharge(transactionData: ZPMSConnector.SalesResourceDto) {
        const response = await this.sendApiRequest<ZPMSConnector.TransactionResponse>('charges', 'POST', transactionData);

        return response;
    }

    /**
     * Creates a document in the PMS.
     * @param transactionData The document data.
     * @returns A promise that resolves to the transaction response.
     */
    public async createDocument(transactionData: ZPMSConnector.DocumentDto) {
        const response = await this.sendApiRequest<ZPMSConnector.DocumentResponse>('document', 'POST', transactionData);

        return response;
    }

    /**
     * Deletes a charge from the PMS.
     * @param charge The charge to delete.
     */
    public async deleteCharge(charge: ZPMSConnector.SalesResourceDto) {
        if(this.config.pmsFeatures?.operations?.['/deletecharges'] !== true) {
            throw 'DELETE_CHARGES_NOT_SUPPORTED';
        }

        const response = await this.sendApiRequest('deletecharges', 'POST', charge);

        return response;
    }
}

@Injectable({
    providedIn: 'root'
})
export class ZPMSConnectorApiService {
    private readonly environmentInfoService = inject(EnvironmentInfoService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly zIdentityService = inject(ZIdentityService);

    /**
     * Sets up the ZPMS Connector API by saving the payment method configuration.
     * @param paymentMethod The payment method object to update.
     * @param config The ZPMS configuration to save.
     */
    public async setupApi(paymentMethod: PaymentMethods, config: ZPMSConfig) {
        Object.assign(paymentMethod, {
            configuration: {
                ...config
            }
        });

        try {
            await this.entityManagerService.paymentMethods.putOneOnline(paymentMethod);
        } catch (err) {
            throw 'SETUP_FAILED';
        }
    }

    /**
     * Checks if the provided configuration contains the required properties.
     * @param config The configuration object to check.
     * @throws 'DIGITAL_PAYMENTS.ZPMS_CONNECTOR.SETUP_REQUIRED' if any required property is missing.
     */
    private checkConfiguration(config: any): asserts config is ZPMSConfig {
        for (const key of ['propertyId', 'connectedPms', 'pmsFeatures']) {
            if (!config?.[key]) {
                throw 'DIGITAL_PAYMENTS.ZPMS_CONNECTOR.SETUP_REQUIRED';
            }
        }

        return config;
    }

    /**
     * Retrieves an instance of `ZPMSApi` for the given payment method.
     * @param paymentMethod The payment method for which to get the API instance.
     * @param options Optional configuration options.
     * @param options.skipConfigCheck Whether to skip the configuration check.
     * @returns A promise that resolves to an instance of `ZPMSApi`.
     */
    public async getApiInstance(paymentMethod: PaymentMethods, options?: { skipConfigCheck?: boolean }) {
        const config = paymentMethod.configuration || {};

        if(!options?.skipConfigCheck) {
            this.checkConfiguration(config);
        }

        return new ZPMSApi(this.zIdentityService, this.environmentInfoService, config);
    }
}